import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Switch,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import AddonPackageBuilder from './AddonPackageBuilder';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const AddonPackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [packageToEdit, setPackageToEdit] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadPackages();
  }, [refreshTrigger]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all packages (both active and inactive) - backend now returns all by default
      const response = await apiGet('/api/addon-packages/?is_reusable=true');
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      } else {
        throw new Error('Failed to load addon packages');
      }
    } catch (err) {
      console.error('Error loading packages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setPackageToEdit(null);
    setBuilderOpen(true);
  };

  const handleEdit = (pkg) => {
    setPackageToEdit(pkg);
    setBuilderOpen(true);
  };

  const handleToggleActive = async (pkg) => {
    try {
      // Prepare payload in the format expected by the API
      const payload = {
        name: pkg.name,
        description: pkg.description || '',
        category: pkg.category || 'Wellness',
        default_discount_type: pkg.default_discount_type || 'none',
        default_discount_value: pkg.default_discount_value || 0,
        tax_rate: pkg.tax_rate || 18.0,
        is_active: !pkg.is_active,
        is_reusable: pkg.is_reusable !== undefined ? pkg.is_reusable : true,
        items: (pkg.items || []).map((item, idx) => ({
          day_number: item.day_number,
          treatment_name: item.treatment_name,
          duration: item.duration || 50,
          order_in_day: item.order_in_day || (idx + 1)
        }))
      };

      const response = await apiPut('/api/addon-packages/${pkg.id}', payload);

      if (response.ok) {
        setRefreshTrigger(t => t + 1);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update package status');
      }
    } catch (err) {
      console.error('Error updating package status:', err);
      alert('Failed to update package status: ' + err.message);
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Are you sure you want to delete "${pkg.name}"?`)) {
      return;
    }

    try {
      const response = await apiDelete(`/api/addon-packages/${pkg.id}`);

      if (response.ok) {
        setRefreshTrigger(t => t + 1);
      } else {
        throw new Error('Failed to delete package');
      }
    } catch (err) {
      console.error('Error deleting package:', err);
      alert('Failed to delete package: ' + err.message);
    }
  };

  const handleBuilderClose = () => {
    setBuilderOpen(false);
    setPackageToEdit(null);
  };

  const handleBuilderSave = () => {
    setBuilderOpen(false);
    setPackageToEdit(null);
    setRefreshTrigger(t => t + 1);
  };

  const filteredPackages = packages.filter(pkg => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      pkg.name.toLowerCase().includes(searchLower) ||
      (pkg.category && pkg.category.toLowerCase().includes(searchLower)) ||
      (pkg.description && pkg.description.toLowerCase().includes(searchLower))
    );
  });

  const getTotalDays = (pkg) => {
    if (!pkg.items || pkg.items.length === 0) return 0;
    return Math.max(...pkg.items.map(item => item.day_number));
  };

  const calculateEstimatedPrice = (pkg) => {
    if (!pkg.estimated_pricing) return 0;
    return pkg.estimated_pricing.total || 0;
  };

  const formatDiscount = (pkg) => {
    if (!pkg.default_discount_type || pkg.default_discount_type === 'none') {
      return <Chip label="None" size="small" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />;
    }
    if (pkg.default_discount_type === 'percentage') {
      return <Chip label={`${pkg.default_discount_value}%`} size="small" sx={{ backgroundColor: '#E4C984', color: '#171717', fontWeight: 600 }} />;
    }
    if (pkg.default_discount_type === 'amount') {
      return <Chip label={`₹${pkg.default_discount_value}`} size="small" sx={{ backgroundColor: '#E4C984', color: '#171717', fontWeight: 600 }} />;
    }
    return '-';
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalOfferIcon sx={{ fontSize: 32, color: '#E4C984' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#171717' }}>
            Addon Package Builder
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            backgroundColor: '#E4C984',
            color: '#171717',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#d4b874'
            }
          }}
        >
          Create Package
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search packages by name, category, or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          size="small"
        />
      </Box>

      {/* Package Summary Stats */}
      {!loading && filteredPackages.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total Packages</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{filteredPackages.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Active Packages</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {filteredPackages.filter(p => p.is_active).length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total Usage</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {filteredPackages.reduce((sum, p) => sum + (p.times_used || 0), 0)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              ₹{filteredPackages.reduce((sum, p) => sum + parseFloat(p.total_revenue || 0), 0).toFixed(2)}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Packages Table */}
      {!loading && (
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Package Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Days</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Treatments</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Est. Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Discount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Tax Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Times Used</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPackages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {search ? 'No packages found matching your search.' : 'No addon packages created yet. Click "Create Package" to get started.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPackages.map((pkg) => (
                    <TableRow 
                      key={pkg.id} 
                      hover
                      sx={{
                        backgroundColor: pkg.is_active ? 'transparent' : '#f9fafb',
                        '&:hover': {
                          backgroundColor: pkg.is_active ? 'rgba(0, 0, 0, 0.04)' : '#f3f4f6'
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: pkg.is_active ? 'inherit' : '#9ca3af' }}>
                            {pkg.name}
                          </Typography>
                          {pkg.description && (
                            <Typography variant="caption" color="text.secondary">
                              {pkg.description.substring(0, 60)}{pkg.description.length > 60 ? '...' : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={pkg.category || 'Other'} 
                          size="small" 
                          variant="outlined"
                          sx={{
                            borderColor: '#E4C984',
                            color: '#6b7280'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{getTotalDays(pkg)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{pkg.items ? pkg.items.length : 0}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: pkg.is_active ? '#E4C984' : '#9ca3af' }}>
                          ₹{calculateEstimatedPrice(pkg).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{formatDiscount(pkg)}</TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{pkg.tax_rate}%</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={pkg.times_used || 0}
                          size="small"
                          sx={{
                            backgroundColor: pkg.times_used > 0 ? '#E4C984' : '#f3f4f6',
                            color: pkg.times_used > 0 ? '#171717' : '#6b7280',
                            fontWeight: pkg.times_used > 0 ? 600 : 400
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={pkg.is_active ? 'Click to deactivate' : 'Click to activate'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Switch
                              checked={pkg.is_active}
                              onChange={() => handleToggleActive(pkg)}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#E4C984',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#E4C984',
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ color: pkg.is_active ? '#E4C984' : '#9ca3af', fontWeight: pkg.is_active ? 600 : 400 }}>
                              {pkg.is_active ? 'Active' : 'Inactive'}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(pkg)}
                          sx={{ color: '#E4C984' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(pkg)}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Builder Modal */}
      <AddonPackageBuilder
        open={builderOpen}
        onClose={handleBuilderClose}
        packageToEdit={packageToEdit}
        onSaved={handleBuilderSave}
      />
    </Box>
  );
};

export default AddonPackageManager;

